import classNames from 'classnames';
import { forwardRef, MouseEventHandler, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import ChevronSm from '../../icons/ChevronSm';
import Close from '../../icons/Close';
import { Size } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import { IconDefinition } from '../IconRenderer';
import styles from './styles/styles.module.scss';

// https://www.figma.com/file/9Y1n3VoMJAcTGPe82bvySj/Denali-Components?node-id=8814%3A7611&mode=dev

type FilterChipProps = {
  className?: string;
  hideChevron?: boolean;
  icon?: IconDefinition<'orientation'>;
  isOpen?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onClear?: MouseEventHandler<HTMLButtonElement>;
  size?: Size<'sm' | 'md'>;
  selectedItems?: ReactNode[];
  unselectedText: ReactNode;
  truncate?: boolean;
};

const FilterChip = (
  { className, hideChevron, icon, isOpen, onClick, onClear, size = 'md', selectedItems, unselectedText, truncate }: FilterChipProps,
  ref: React.Ref<HTMLDivElement>
) => {
  const intl = useIntl();
  const isSelected = selectedItems && selectedItems.length > 0;
  const text = isSelected ? selectedItems[0] : unselectedText;
  const selectedOverflowCount = isSelected && selectedItems.length > 1 ? `+${selectedItems.length - 1}` : null;
  const noChevron = hideChevron || isSelected;
  const buttonClasses = classNames(styles.button, styles[size], { [styles.noChevron]: noChevron });
  return (
    <div
      {...setDenaliAttribute('FilterChip')}
      className={classNames(styles.container, className, { [styles.selected]: isSelected, [styles.truncate]: truncate })}
      ref={ref}
    >
      <button className={buttonClasses} onClick={onClick} type="button">
        {icon && <icon.Component size={size} orientation={icon.orientation} />}
        <div className={styles.textContent}>
          <span>{text}</span>
          {selectedOverflowCount && <span>{selectedOverflowCount}</span>}
        </div>
        {noChevron ? null : <ChevronSm orientation={isOpen ? 'up' : 'down'} color="currentColor" size={size} />}
      </button>
      {isSelected && onClear ? (
        <button
          className={buttonClasses}
          onClick={onClear}
          type="button"
          title={intl.formatMessage({ defaultMessage: 'Close' })}
          aria-label={intl.formatMessage({ defaultMessage: 'Close' })}
        >
          <Close color="currentColor" size={size} />
        </button>
      ) : null}
    </div>
  );
};

export default forwardRef(FilterChip);
